import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import { Button } from '../../components/ui/Button';
import supabase from '../../supabase/supabase';
import Toast from '../../components/ui/Toast';
import { useToast } from '../../hooks/useToast';

const LOGO_URL = "https://res.cloudinary.com/dfg8nrgvf/image/upload/q_auto/f_auto/v1780693784/BOTG_Logo_o9fcqe.png";

export default function RequestDeletion() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const { toast, showToast, hideToast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('deletion_requests_1740652338909')
        .insert([{ 
          email: email.trim().toLowerCase(), 
          reason: reason.trim(),
          user_id: user?.id || null
        }]);

      if (error) throw error;
      setSubmitted(true);
    } catch (err) {
      showToast("Error submitting request: " + err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white p-10 rounded-[2.5rem] shadow-xl border border-gray-100 max-w-md w-full text-center animate-in zoom-in-95">
          <div className="w-20 h-20 bg-blue-50 text-brand-primary rounded-full flex items-center justify-center mb-6 mx-auto">
            <SafeIcon name="Check" className="text-4xl" />
          </div>
          <h1 className="text-2xl font-black text-brand-dark mb-2">Request Received</h1>
          <p className="text-gray-500 mb-8 font-medium">
            Your request to delete account <strong>{email}</strong> has been logged. Our team will process this within 7 business days.
          </p>
          <Button onClick={() => navigate('/')} className="w-full">Return Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 flex flex-col justify-center items-center relative">
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
      <div className="max-w-md w-full">
        
        <div className="flex justify-center mb-10 cursor-pointer" onClick={() => navigate('/')}>
          <img src={LOGO_URL} alt="BookOTG" className="h-16 object-contain" />
        </div>

        <div className="bg-white rounded-[2.5rem] shadow-xl border border-gray-100 p-8 sm:p-10 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-2 bg-red-500"></div>
          
          <h1 className="text-2xl font-black text-brand-dark tracking-tight mb-2">Delete My Account</h1>
          <p className="text-gray-500 text-sm font-medium mb-8 leading-relaxed">
            We're sorry to see you go. Requesting account deletion will permanently remove your profile, booking history, and active services. <strong>This action cannot be undone.</strong>
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest px-1">Account Email</label>
              <input 
                type="email" 
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-5 py-4 border border-gray-200 rounded-2xl bg-gray-50 outline-none focus:border-brand-primary font-bold text-brand-dark"
                placeholder="The email you used to sign up"
              />
            </div>
            
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest px-1">Reason for leaving (Optional)</label>
              <textarea 
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-5 py-4 border border-gray-200 rounded-2xl bg-gray-50 outline-none focus:border-brand-primary font-medium text-brand-dark min-h-[100px]"
                placeholder="Tell us how we can improve..."
              />
            </div>

            <Button type="submit" variant="danger" className="w-full py-5 text-lg font-black rounded-2xl shadow-xl shadow-red-500/10" disabled={loading}>
              {loading ? 'Submitting...' : 'Confirm Deletion Request'}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-gray-100 text-center">
            <button onClick={() => navigate(-1)} className="text-sm font-bold text-gray-400 hover:text-brand-dark transition-colors">
              Cancel and Go Back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}